import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useRecoilValue} from 'recoil';
import {ESwitch, EUndecoratedButton} from 'webapp-lib/Components';
import {useMountEffect} from 'webapp-lib/utils';

import {ErrorMessage, SubTabBar} from '#';
import {TransparentBackground} from '#/TransparentBackground';
import {
  Notification,
  NotificationSettings,
} from '@lexian/api/notifications-api';
import {I18NextNS} from '_/i18n';
import {userState} from '_/state/user';
import {State} from '_/utilities';

import {NotificationsButton} from './components/NotificationsButton';
import {NotificationsList, NotificationView} from './screens';
import {Store, StoreContext} from './store';
import {
  ClearInfoNotificationsButton,
  FiltersWrapper,
  NotificationsWrapper,
  StyledNotificationOverview,
} from './styles';
import {SetVisibilityContext} from './utilities/context';

type ActiveItemType = {
  id: NotificationSettings['visibleNotifications'];
  text: string;
};

type NotificationsProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

const UNotifications = ({
  isOpen,
  setIsOpen,
}: NotificationsProps): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('notifications');
  const user = useRecoilValue(userState);
  const [store] = useState(() => new Store(user));
  // Unsubscribe on unmount only
  useMountEffect(() => () => store.unsubscribe());

  const updateImportantFlag = useCallback(
    (value: boolean) => {
      store.updateSettings({...store.settings, showOnlyImportant: value});
    },
    [store]
  );

  const updateGroupedFlag = useCallback(
    (value: boolean) => {
      store.updateSettings({...store.settings, grouped: value});
    },
    [store]
  );

  const clearActiveNotification = useCallback(() => {
    store.setActiveNotification(undefined);
  }, [store]);

  const openNotification = useCallback(
    (notification: Notification) => {
      store.setActiveNotification(notification);
      store.setReadFlag({
        newFlag: true,
        notificationId: notification.id,
      });
    },
    [store]
  );
  const clearAllNotifications = useCallback(() => {
    store.clearNotifications();
  }, [store]);

  const subNavigationItems: ActiveItemType[] = useMemo(
    () => [
      {
        id: 'ALL',
        text: t('filterTabs.all'),
      },
      {
        id: 'RESOLVABLE',
        text: t('filterTabs.resolvable', {
          amountOfNotifications:
            store.summary.data?.unreadResolvableNotifications,
        }),
      },
      {
        id: 'INFORMATIVE',
        text: t('filterTabs.informative', {
          amountOfNotifications:
            store.summary.data?.unreadInformativeNotifications,
        }),
      },
    ],
    [
      store.summary.data?.unreadInformativeNotifications,
      store.summary.data?.unreadResolvableNotifications,
      t,
    ]
  );

  const activeSubNavigationItem = useMemo(
    () =>
      subNavigationItems.find(
        (item) => item.id === store.settings.visibleNotifications
      )!,
    [store.settings.visibleNotifications, subNavigationItems]
  );

  const activeTabChangeHandler = useCallback(
    (item: ActiveItemType) => {
      store.updateSettings({
        ...store.settings,
        visibleNotifications:
          item.id as NotificationSettings['visibleNotifications'],
      });
    },
    [store]
  );

  useEffect(() => {
    if (isOpen) {
      store.fetchNotifications();
      store.setModuleState(true);
    } else {
      store.setModuleState(false);
    }
  }, [isOpen, store]);

  const toggleNotifications = useCallback(
    () => setIsOpen(!isOpen),
    [isOpen, setIsOpen]
  );

  const close = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  if (store.notifications.state === State.Error) {
    return (
      <ErrorMessage
        message={t('errors.loadNotificationsFailed')}
        onRetry={store.fetchNotifications}
      />
    );
  }

  return (
    <StoreContext.Provider value={store}>
      <SetVisibilityContext.Provider value={{close}}>
        <NotificationsButton
          isActive={isOpen}
          summary={store.summary}
          onClick={toggleNotifications}
        />
        {isOpen ? (
          <>
            <TransparentBackground onMouseUp={close} />
            <NotificationsWrapper>
              {store.activeNotification ? (
                <NotificationView
                  notification={store.activeNotification}
                  onBack={clearActiveNotification}
                />
              ) : (
                <StyledNotificationOverview>
                  <SubTabBar
                    activeTabItem={activeSubNavigationItem}
                    items={subNavigationItems}
                    fullWidth
                    onChange={activeTabChangeHandler}
                  />
                  <FiltersWrapper>
                    {t('common:actions.filter')}
                    :
                    <ESwitch
                      checked={store.settings.showOnlyImportant}
                      label={t('label.importantSwitch')}
                      onChange={(_, checked) => updateImportantFlag(checked)}
                    />
                    <ESwitch
                      checked={store.settings.grouped}
                      label={t('label.groupedSwitch')}
                      onChange={(_, checked) => updateGroupedFlag(checked)}
                    />
                    {store.notifications.data?.some(
                      (item) => item.type === 'INFORMATIVE'
                    ) ? (
                      <ClearInfoNotificationsButton>
                        <EUndecoratedButton onClick={clearAllNotifications}>
                          {t('button.clearInformative')}
                        </EUndecoratedButton>
                      </ClearInfoNotificationsButton>
                    ) : null}
                  </FiltersWrapper>
                  <NotificationsList onChange={openNotification} />
                </StyledNotificationOverview>
              )}
            </NotificationsWrapper>
          </>
        ) : null}
      </SetVisibilityContext.Provider>
    </StoreContext.Provider>
  );
};

export default observer(UNotifications);
